/**
 * MODIFIED: 2025-01-29 - refactor/task-1.5-dashboard-performance
 *
 * ARCHITECTURE: API Architecture
 * PURPOSE: Centralized API path construction with parameter replacement and query encoding
 *
 * NEW PATTERNS IMPLEMENTED:
 * - ApiClient Pattern: Centralized HTTP handler with generic types
 * - Error Handling: Comprehensive ApiError parsing with field-level errors
 * - Token Refresh: Automatic retry on 401 with exponential backoff
 * - Safe Variants: Tuple return [data | null, error | null]
 * - Path Builder: Functional endpoint construction via API_CONFIG
 *
 * KEY FEATURES:
 * - Type-safe path parameter replacement
 * - Consistent query parameter encoding
 * - Array parameter support
 * - Alphabetical key sorting for consistent URLs
 * - Environment-based base URL configuration
 * - API version management
 * - Null/undefined filtering
 *
 * RELATED FILES:
 * - src/lib/services/api/client.ts
 * - src/lib/config/apiConfig.ts
 */

import { PUBLIC_API_URL } from '$env/static/public';

/**
 * Centralized API path builder with version management
 *
 * Purpose: Eliminates 109+ hardcoded path constructions across the codebase
 * Benefits:
 * - Single source of truth for API base URL and version
 * - Type-safe path parameter replacement
 * - Consistent query parameter encoding
 * - Easy API version migrations (v1 → v2)
 *
 * @example
 * ```typescript
 * // Path with parameters
 * apiPath.path('/alerts/:id/approve', { id: '123' })
 * // → '/api/v1/alerts/123/approve'
 *
 * // Path with query parameters
 * const path = apiPath.path('/alerts');
 * apiPath.withQuery(path, { status: 'active', limit: 10 })
 * // → '/api/v1/alerts?limit=10&status=active'
 * ```
 */

/**
 * API path builder for constructing consistent API endpoints
 */
class ApiPathBuilder {
	private baseUrl: string;
	private apiVersion: string;

	constructor(baseUrl: string = '', apiVersion: string = 'v1') {
		this.baseUrl = baseUrl;
		this.apiVersion = apiVersion;
	}

	/**
	 * Build a complete API path with parameter replacement
	 *
	 * Replaces path parameters (`:param`) with URL-encoded actual values and constructs
	 * the full API path including base URL and version.
	 *
	 * **IMPORTANT:** All parameter values are automatically URL-encoded to handle special
	 * characters (colons, timestamps, spaces, etc.) in alert IDs and other parameters.
	 *
	 * @param endpoint - The endpoint path (e.g., '/alerts/:id/approve')
	 * @param params - Optional path parameters to replace (e.g., { id: '123' })
	 * @returns The complete API path with URL-encoded parameters (e.g., '/api/v1/alerts/123/approve')
	 * @throws {Error} If path contains unreplaced parameters
	 *
	 * @example
	 * ```typescript
	 * // Simple parameter
	 * apiPath.path('/alerts/:id/approve', { id: '123' })
	 * // → '/api/v1/alerts/123/approve'
	 *
	 * // Multiple parameters
	 * apiPath.path('/alerts/:id/:action', { id: '123', action: 'reject' })
	 * // → '/api/v1/alerts/123/reject'
	 *
	 * // Complex alert ID with special characters (URL-encoded automatically)
	 * apiPath.path('/alerts/:alertId/insights', {
	 *   alertId: '2025-08-13T15:35:36_5a7069c6_237020'
	 * })
	 * // → '/api/v1/alerts/2025-08-13T15%3A35%3A36_5a7069c6_237020/insights'
	 *
	 * // Error: Missing parameter
	 * apiPath.path('/alerts/:id/approve', {})
	 * // → Error: Path contains unreplaced parameters: /api/v1/alerts/:id/approve
	 * ```
	 */
	path(endpoint: string, params?: Record<string, string | number>): string {
		let path = endpoint;

		// Replace path parameters with URL-encoded values
		if (params) {
			for (const [key, value] of Object.entries(params)) {
				// 🔧 FIX: URL-encode parameter values to handle special characters
				// This handles alert IDs with timestamps, colons, underscores, etc.
				const encodedValue = encodeURIComponent(String(value));
				path = path.replace(`:${key}`, encodedValue);
			}
		}

		// Validate all parameters were replaced
		if (path.includes(':')) {
			throw new Error(`Path contains unreplaced parameters: ${path}`);
		}

		return `${this.baseUrl}/api/${this.apiVersion}${path}`;
	}

	/**
	 * Add query parameters to a path
	 *
	 * Filters out null/undefined values and properly encodes all parameters.
	 * Supports arrays (adds multiple values for same key).
	 * Sorts keys alphabetically for consistent URLs and better caching.
	 *
	 * @param path - The base path (from `path()` method)
	 * @param query - Query parameters as key-value pairs
	 * @returns The path with query string appended
	 *
	 * @example
	 * ```typescript
	 * // Simple query parameters
	 * apiPath.withQuery('/api/v1/alerts', { status: 'active', limit: 10 })
	 * // → '/api/v1/alerts?limit=10&status=active'
	 *
	 * // Array parameters
	 * apiPath.withQuery('/api/v1/alerts', { ids: ['1', '2', '3'] })
	 * // → '/api/v1/alerts?ids=1&ids=2&ids=3'
	 *
	 * // Null/undefined filtering
	 * apiPath.withQuery('/api/v1/alerts', { status: 'active', filter: null, sort: undefined })
	 * // → '/api/v1/alerts?status=active'
	 *
	 * // Sorted keys (consistent URLs for caching)
	 * apiPath.withQuery('/api/v1/alerts', { z: 1, a: 2, m: 3 })
	 * // → '/api/v1/alerts?a=2&m=3&z=1'
	 * ```
	 */
	withQuery(path: string, query: Record<string, unknown>): string {
		const url = new URL(path, path.startsWith('http') ? undefined : window.location.origin);

		// Sort keys for consistent URLs (better for caching)
		const sortedKeys = Object.keys(query).sort();

		for (const key of sortedKeys) {
			const value = query[key];

			// Filter out null/undefined
			if (value !== undefined && value !== null) {
				if (Array.isArray(value)) {
					// Handle arrays: append multiple values
					value.forEach((v) => url.searchParams.append(key, String(v)));
				} else {
					// Handle single values
					url.searchParams.set(key, String(value));
				}
			}
		}

		return url.pathname + url.search;
	}

	/**
	 * Get the current base URL
	 * @returns The base URL (e.g., '' for relative paths or 'http://localhost:8000')
	 */
	getBaseUrl(): string {
		return this.baseUrl;
	}

	/**
	 * Get the current API version
	 * @returns The API version (e.g., 'v1')
	 */
	getApiVersion(): string {
		return this.apiVersion;
	}
}

/**
 * Singleton instance of ApiPathBuilder
 *
 * Uses environment variable for base URL configuration:
 * - Development: PUBLIC_API_URL from .env (http://localhost:8000)
 * - Production: PUBLIC_API_URL from .env.production (https://api.aspenpower-amos.com)
 * - Fallback: '' (empty string for relative paths to Vite proxy)
 */
export const apiPath = new ApiPathBuilder(
	PUBLIC_API_URL || '',
	'v1'
);

/**
 * Export class for testing purposes
 */
export { ApiPathBuilder };
